import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import PageLoader from '../components/PageLoader';
import { AppActionBar, AppButton } from '../components/authenticated-ui/AuthenticatedUi';
import { apiJson, isUnauthorizedError } from '../services/api';
import './PropertyList.css';

const fmt = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const readSortOrder = (property) => {
  const value = Number(property?.sort_order);
  return Number.isFinite(value) ? value : null;
};
const createdAtTimestamp = (property) => {
  const value = Date.parse(property?.created_at || '');
  return Number.isFinite(value) ? value : 0;
};
const sortPropertiesForGrid = (items) => [...items].sort((left, right) => {
  const leftSortOrder = readSortOrder(left);
  const rightSortOrder = readSortOrder(right);
  if (leftSortOrder != null && rightSortOrder != null && leftSortOrder !== rightSortOrder) {
    return leftSortOrder - rightSortOrder;
  }
  if (leftSortOrder != null) return -1;
  if (rightSortOrder != null) return 1;

  const createdAtDifference = createdAtTimestamp(right) - createdAtTimestamp(left);
  if (createdAtDifference !== 0) return createdAtDifference;
  return Number(right.id) - Number(left.id);
});
const reorderProperties = (items, draggedPropertyId, targetPropertyId) => {
  const draggedIndex = items.findIndex((property) => property.id === draggedPropertyId);
  const targetIndex = items.findIndex((property) => property.id === targetPropertyId);
  if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) return items;

  const nextItems = [...items];
  const [draggedProperty] = nextItems.splice(draggedIndex, 1);
  nextItems.splice(targetIndex, 0, draggedProperty);
  return nextItems;
};
const hasOrderChanged = (leftItems, rightItems) => {
  if (leftItems.length !== rightItems.length) return true;
  return leftItems.some((property, index) => property.id !== rightItems[index]?.id);
};

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [failedImages, setFailedImages] = useState({});
  const [photoIndexes, setPhotoIndexes] = useState({});
  const [loading, setLoading] = useState(true);
  const [draggedPropertyId, setDraggedPropertyId] = useState(null);
  const [dropTargetPropertyId, setDropTargetPropertyId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const navigate = useNavigate();
  const toast = useRef(null);
  const propertiesRef = useRef([]);
  const originalPropertiesRef = useRef([]);
  const draggedPropertyIdRef = useRef(null);
  const dropCommittedRef = useRef(false);
  const lastHoveredPropertyIdRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => {
    propertiesRef.current = properties;
  }, [properties]);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiJson('/api/properties');
        setProperties(sortPropertiesForGrid(Array.isArray(data) ? data : []));
      } catch (error) {
        if (isUnauthorizedError(error)) {
          navigate('/login', { replace: true });
          return;
        }
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load properties', life: 4000 });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const markImageFailed = (photoUrl) => {
    setFailedImages((current) => (
      current[photoUrl]
        ? current
        : { ...current, [photoUrl]: true }
    ));
  };

  const movePhoto = (event, propertyId, direction, photoCount) => {
    event.preventDefault();
    event.stopPropagation();
    setPhotoIndexes((current) => ({
      ...current,
      [propertyId]: ((current[propertyId] || 0) + direction + photoCount) % photoCount,
    }));
  };

  const resetDragState = () => {
    draggedPropertyIdRef.current = null;
    dropCommittedRef.current = false;
    lastHoveredPropertyIdRef.current = null;
    setDraggedPropertyId(null);
    setDropTargetPropertyId(null);
  };

  const persistPropertyOrder = async (nextProperties, previousProperties) => {
    setSavingOrder(true);
    try {
      await apiJson('/api/properties/reorder', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ property_ids: nextProperties.map((property) => property.id) }),
      });
    } catch (error) {
      setProperties(previousProperties);
      propertiesRef.current = previousProperties;
      if (isUnauthorizedError(error)) {
        navigate('/login', { replace: true });
        return;
      }
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save property order',
        life: 4000,
      });
    } finally {
      setSavingOrder(false);
    }
  };

  const finalizePropertyDrop = async () => {
    const previousProperties = originalPropertiesRef.current.slice();
    const nextProperties = propertiesRef.current.slice();
    const orderChanged = hasOrderChanged(previousProperties, nextProperties);
    suppressClickRef.current = orderChanged;
    resetDragState();
    if (!orderChanged) return;
    await persistPropertyOrder(nextProperties, previousProperties);
  };

  const handleCardClick = (propertyId) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    navigate(`/properties/${propertyId}`);
  };

  const handleDragStart = (propertyId, event) => {
    if (savingOrder || properties.length < 2) {
      event.preventDefault();
      return;
    }
    draggedPropertyIdRef.current = propertyId;
    originalPropertiesRef.current = propertiesRef.current.slice();
    dropCommittedRef.current = false;
    lastHoveredPropertyIdRef.current = propertyId;
    suppressClickRef.current = false;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(propertyId));
    setDraggedPropertyId(propertyId);
    setDropTargetPropertyId(propertyId);
  };

  const handleCardDragOver = (targetPropertyId, event) => {
    event.preventDefault();
    if (!draggedPropertyIdRef.current || savingOrder) return;
    event.dataTransfer.dropEffect = 'move';
    if (
      draggedPropertyIdRef.current === targetPropertyId
      || lastHoveredPropertyIdRef.current === targetPropertyId
    ) {
      return;
    }

    lastHoveredPropertyIdRef.current = targetPropertyId;
    suppressClickRef.current = true;
    setDropTargetPropertyId(targetPropertyId);
    setProperties((current) => {
      const next = reorderProperties(current, draggedPropertyIdRef.current, targetPropertyId);
      if (next === current) return current;
      propertiesRef.current = next;
      return next;
    });
  };

  const handleGridDrop = (event) => {
    event.preventDefault();
    if (!draggedPropertyIdRef.current) return;
    dropCommittedRef.current = true;
    void finalizePropertyDrop();
  };

  const handleDragEnd = () => {
    if (!draggedPropertyIdRef.current) return;
    if (!dropCommittedRef.current) {
      const previousProperties = originalPropertiesRef.current.slice();
      setProperties(previousProperties);
      propertiesRef.current = previousProperties;
      suppressClickRef.current = false;
      resetDragState();
    }
  };

  if (loading) return <PageLoader label="Loading properties" />;

  return (
    <div className="pl-page">
      <Toast ref={toast} position="top-right" />
      <div className="pl-header">
        <div>
          <p className="pl-eyebrow">{properties.length} {properties.length === 1 ? 'property' : 'properties'} in portfolio</p>
          <h1>Properties</h1>
      
          {/* <p className="pl-subtitle">{properties.length} {properties.length === 1 ? 'property' : 'properties'} in portfolio</p> */}
        </div>
        <button className="pl-btn-add" onClick={() => navigate('/add-property')}>
          <i className="pi pi-plus" /> Add Property
        </button>
      </div>

      {properties.length === 0 ? (
        <div className="pl-empty">
          <i className="pi pi-building" style={{ fontSize: '3rem', color: '#D1D5DB' }} />
          <p>No properties yet.</p>
          <button className="pl-btn-add" onClick={() => navigate('/add-property')}>Add your first property</button>
        </div>
      ) : (
        <div
          className={`pl-grid${draggedPropertyId ? ' pl-grid--sorting' : ''}${savingOrder ? ' pl-grid--saving' : ''}`}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleGridDrop}
        >
          {properties.map(p => {
            const appreciation = p.current_value
              ? parseFloat(p.current_value) - parseFloat(p.purchase_price)
              : null;
            const propertyPhotos = (Array.isArray(p.photo_urls) && p.photo_urls.length
              ? p.photo_urls
              : [p.primary_photo_url]
            ).filter((url) => url && !failedImages[url]);
            const photoIndex = propertyPhotos.length
              ? (photoIndexes[p.id] || 0) % propertyPhotos.length
              : 0;
            const photoUrl = propertyPhotos[photoIndex] || null;
            const isDragging = draggedPropertyId === p.id;
            const isDropTarget = dropTargetPropertyId === p.id && draggedPropertyId && !isDragging;
            return (
              <div
                key={p.id}
                className={`pl-card${isDragging ? ' pl-card--dragging' : ''}${isDropTarget ? ' pl-card--drop-target' : ''}`}
                draggable={!savingOrder && properties.length > 1}
                onClick={() => handleCardClick(p.id)}
                onDragStart={(event) => handleDragStart(p.id, event)}
                onDragOver={(event) => handleCardDragOver(p.id, event)}
                onDragEnd={handleDragEnd}
              >
                {/* Photo thumbnail */}
                <div className="pl-card-photo">
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                      onError={() => markImageFailed(photoUrl)}
                    />
                  ) : (
                    <div className="pl-card-photo-placeholder">
                      <i className="pi pi-image" />
                    </div>
                  )}
                  {propertyPhotos.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="pl-photo-nav pl-photo-nav--previous"
                        aria-label={`Previous photo for ${p.nickname || p.address_line1}`}
                        onClick={(event) => movePhoto(event, p.id, -1, propertyPhotos.length)}
                        onDragStart={(event) => event.preventDefault()}
                      >
                        <i className="pi pi-chevron-left" />
                      </button>
                      <button
                        type="button"
                        className="pl-photo-nav pl-photo-nav--next"
                        aria-label={`Next photo for ${p.nickname || p.address_line1}`}
                        onClick={(event) => movePhoto(event, p.id, 1, propertyPhotos.length)}
                        onDragStart={(event) => event.preventDefault()}
                      >
                        <i className="pi pi-chevron-right" />
                      </button>
                      <span className="pl-photo-count">{photoIndex + 1} / {propertyPhotos.length}</span>
                    </>
                  )}
                  <span className="pl-badge">{p.property_type?.replace('_', ' ')}</span>
                </div>

                <div className="pl-card-body">
                  <div className="pl-card-header">
                    <h2 className="pl-card-title">{p.nickname || p.address_line1}</h2>
                    <p className="pl-card-address">{p.address_line1}, {p.city}, {p.state}</p>
                    <div className="pl-card-property-meta">
                      <span><strong>{p.bedrooms ?? '—'}</strong> beds</span>
                      <span><strong>{p.bathrooms ?? '—'}</strong> baths</span>
                      <span><strong>{p.square_feet == null ? '—' : Number(p.square_feet).toLocaleString()}</strong> sq ft</span>
                    </div>
                  </div>
                  <div className="pl-card-stats">
                    <div className="pl-stat">
                      <span className="pl-stat-label">Asking Price</span>
                      <span className="pl-stat-value">{fmt(p.asking_price ?? p.purchase_price)}</span>
                    </div>
                    <div className="pl-stat">
                      <span className="pl-stat-label">Purchase Price</span>
                      <span className="pl-stat-value">{fmt(p.purchase_price)}</span>
                    </div>
                    <div className="pl-stat">
                      <span className="pl-stat-label">Current Value</span>
                      <span className="pl-stat-value">{fmt(p.current_value)}</span>
                    </div>
                    <div className="pl-stat">
                      <span className="pl-stat-label">Appreciation</span>
                      <span className={`pl-stat-value ${appreciation == null ? '' : appreciation >= 0 ? 'green' : 'red'}`}>
                        {appreciation == null ? '—' : `${appreciation >= 0 ? '+' : ''}${fmt(appreciation)}`}
                      </span>
                    </div>
                  </div>
                </div>
                <AppActionBar className="pl-card-actions" onClick={(event) => event.stopPropagation()} onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}>
                  <AppButton variant="accent" label="Rehab" icon="pi pi-sitemap" onClick={() => navigate(`/properties/${p.id}/rehab`)} />
                  <AppButton variant="primary" label="Maintenance" icon="pi pi-wrench" onClick={() => navigate(`/properties/${p.id}`)} />
                </AppActionBar>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PropertyList;
